export type HostMode = 'platform' | 'storefront'

export type HostDetection = {
  mode: HostMode
  slug: string | null
  source: 'subdomain' | 'path' | null
  hostname: string
  baseDomain: string
}

// Subdomains that route to the PLATFORM app, never a storefront. Small,
// deploy-controlled set — deliberately NOT a copy of the DB reserved_slugs list
// (mig 282). Reserved-but-non-platform words (test, mail, blog, ...) are simply
// storefront candidates that resolve to "shop not found" once the D3.1 tenant
// lookup returns nothing, so they do not belong here.
export const PLATFORM_SUBDOMAINS = new Set<string>(['www', 'admin', 'app', 'api'])

const SHOP_PATH = /^\/shop\/([^/]+)/

function platform(hostname: string, baseDomain: string): HostDetection {
  return { mode: 'platform', slug: null, source: null, hostname, baseDomain }
}

export function detectHost(
  rawHostname: string,
  pathname: string,
  baseDomain: string,
): HostDetection {
  const hostname = (rawHostname || '')
    .toLowerCase()
    .replace(/\.$/, '')     // strip trailing dot (FQDN form)
    .replace(/:\d+$/, '')   // strip :port if a caller passed host instead of hostname
  const base = (baseDomain || '').toLowerCase().replace(/\.$/, '')

  // 1) Subdomain storefront (wins over path).
  if (base && hostname !== base && hostname.endsWith('.' + base)) {
    const label = hostname.slice(0, hostname.length - base.length - 1)
    // A real storefront slug is a single DNS label. Multi-label (a.b.palstro.com)
    // cannot get a valid wildcard cert and is not a storefront -> fail safe to
    // platform. Platform subdomains (www/admin/...) also route to platform.
    if (label && !label.includes('.') && !PLATFORM_SUBDOMAINS.has(label)) {
      return { mode: 'storefront', slug: label, source: 'subdomain', hostname, baseDomain: base }
    }
    return platform(hostname, base)
  }

  // 2) Path-based storefront (palstro.com/shop/:slug, localhost/shop/:slug, ...).
  const m = pathname.match(SHOP_PATH)
  if (m) {
    const slug = decodeURIComponent(m[1]).toLowerCase()
    if (slug) {
      return { mode: 'storefront', slug, source: 'path', hostname, baseDomain: base }
    }
  }

  // 3) Everything else — apex, www/admin, *.vercel.app previews, localhost, IPs.
  return platform(hostname, base)
}

// Phase 0.3 — is this host a CANDIDATE custom storefront domain (a merchant-owned
// domain like larry-retails.com) that we should ask the server to resolve? True only
// for a host that is NOT part of the platform's own space and NOT a dev/preview host:
//   • not the base domain or any *.baseDomain (those are platform/subdomain-storefront,
//     already handled by detectHost)
//   • not localhost / *.localhost
//   • not an IPv4 literal
//   • not a *.vercel.app preview deployment
// Pure + synchronous so it can gate the async resolve_storefront_host lookup without a
// wasted round-trip on every platform page load.
export function isCustomHostCandidate(rawHostname: string, baseDomain: string): boolean {
  const hostname = (rawHostname || '')
    .toLowerCase()
    .replace(/\.$/, '')
    .replace(/:\d+$/, '')
  const base = (baseDomain || '').toLowerCase().replace(/\.$/, '')
  if (!hostname) return false
  if (base && (hostname === base || hostname.endsWith('.' + base))) return false
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) return false
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return false
  if (hostname.endsWith('.vercel.app')) return false
  // Must at least look like a real multi-label hostname before we bother the server.
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(hostname)
}
